import { useProjectStore } from '../../store/useProjectStore';
import { useSSEStore } from '../../store/useSSEStore';
import type { SkillCardData, SkillStatus } from '../../store/useSSEStore';
import { useAuthStore } from '../../store/useAuthStore';
import { SKILL_NAMES, SKILL_LABELS } from '../../types';
import { useEffect, useRef, useState } from 'react';
import * as api from '../../api/endpoints';

export function Sidebar() {
  const collapsed = useProjectStore((s) => s.sidebarCollapsed);
  const setCollapsed = useProjectStore((s) => s.setSidebarCollapsed);

  return (
    <aside className={`app-sidebar ${collapsed ? 'collapsed' : ''}`}>
      {collapsed ? (
        <button className="sidebar-toggle" onClick={() => setCollapsed(false)} title="Expand sidebar">
          &#9656;
        </button>
      ) : (
        <>
          <div className="sidebar-header">
            <span className="sidebar-title">Pipeline</span>
            <button className="sidebar-toggle" onClick={() => setCollapsed(true)} title="Collapse sidebar">
              &#9666;
            </button>
          </div>
          <PipelineHealth />
          <SkillStatusList />
          <FileManifest />
        </>
      )}
    </aside>
  );
}

function PipelineHealth() {
  const project = useProjectStore((s) => s.project);
  const triage = useProjectStore((s) => s.triage);
  const extraction = useProjectStore((s) => s.extraction);

  const fileCount = project?.files?.length || 0;
  const pageCount = triage?.classifications?.length || 0;
  const componentCount = extraction?.zones?.reduce(
    (sum, z) => sum + (z.all_components?.length || 0), 0,
  ) || 0;

  return (
    <div className="sidebar-section">
      <div className="sidebar-health-cards">
        <div className="health-card">
          <span className="health-value">{fileCount}</span>
          <span className="health-label">Files</span>
        </div>
        <div className="health-card">
          <span className="health-value">{pageCount}</span>
          <span className="health-label">Pages</span>
        </div>
        <div className="health-card">
          <span className="health-value">{componentCount}</span>
          <span className="health-label">Components</span>
        </div>
      </div>
    </div>
  );
}

const RESEARCH_SKILLS = ['pre_scan', 'triage', 'separation', 'boq_extraction', 'research_report'] as const;
const EXTRACTION_SKILLS = ['diagram_extraction', 'floor_plan_annotation'] as const;

// A skill is "done" when it has a live terminal status OR its result data is
// already loaded in the store. The store is refreshed by reloadSkillResult on
// every skill_complete, so this also greens skills that finished outside the
// agent loop (for example the incremental "analyze new files" flow), where no
// live status is ever written. Live running/failed always win so a re-run
// shows its spinner. floor_plan_annotation is deliberately absent from
// resultsPresent (it is human-driven — having data does not mean "done").
function resolveSkillStatus(
  name: string,
  statuses: Record<string, SkillCardData>,
  resultsPresent: Set<string>,
): SkillStatus {
  const live = statuses[name]?.status;
  if (live && live !== 'pending') return live;
  return resultsPresent.has(name) ? 'success' : 'pending';
}

function useResultsPresent(): Set<string> {
  const preScan = useProjectStore((state) => state.preScan);
  const triage = useProjectStore((state) => state.triage);
  const separation = useProjectStore((state) => state.separation);
  const boqExtraction = useProjectStore((state) => state.boqExtraction);
  const researchReport = useProjectStore((state) => state.researchReport);
  const extraction = useProjectStore((state) => state.extraction);

  const present = new Set<string>();
  if (preScan) present.add('pre_scan');
  if (triage) present.add('triage');
  if (separation) present.add('separation');
  if (boqExtraction) present.add('boq_extraction');
  if (researchReport) present.add('research_report');
  if (extraction) present.add('diagram_extraction');
  return present;
}

function SkillStatusList() {
  const skillStatuses = useSSEStore((s) => s.skillStatuses);
  const resultsPresent = useResultsPresent();

  const statusOf = (skillName: string) => resolveSkillStatus(skillName, skillStatuses, resultsPresent);
  const done = SKILL_NAMES.filter((skillName) => {
    const status = statusOf(skillName);
    return status === 'success' || status === 'skipped';
  }).length;
  const running = SKILL_NAMES.filter((skillName) => statusOf(skillName) === 'running').length;
  const failed = SKILL_NAMES.filter((skillName) => statusOf(skillName) === 'failed').length;

  return (
    <div className="sidebar-section">
      <div className="sidebar-section-label">
        Skills
        <span className="sidebar-skill-progress">
          {done}/{SKILL_NAMES.length}
          {running > 0 && <span className="progress-running"> ({running} running)</span>}
          {failed > 0 && <span className="progress-failed"> ({failed} failed)</span>}
        </span>
      </div>
      <SkillPhaseGroup label="Research" skills={RESEARCH_SKILLS} statuses={skillStatuses} resultsPresent={resultsPresent} />
      <SkillPhaseGroup label="Extraction" skills={EXTRACTION_SKILLS} statuses={skillStatuses} resultsPresent={resultsPresent} />
    </div>
  );
}

function SkillPhaseGroup({ label, skills, statuses, resultsPresent }: {
  label: string;
  skills: readonly string[];
  statuses: Record<string, SkillCardData>;
  resultsPresent: Set<string>;
}) {
  const isOwner = useAuthStore((s) => s.user?.role) === 'owner';
  const [rerunning, setRerunning] = useState(false);
  const statusOf = (skillName: string) => resolveSkillStatus(skillName, statuses, resultsPresent);
  const done = skills.filter((skillName) => {
    const status = statusOf(skillName);
    return status === 'success' || status === 'skipped';
  }).length;
  const anyRunning = skills.some((skillName) => statusOf(skillName) === 'running');
  const allDone = done === skills.length && done > 0;

  const phase = label.toLowerCase() as 'research' | 'extraction';

  const handleRerun = async () => {
    setRerunning(true);
    try {
      await api.rerunPhase(phase);
      // Clear relevant store data
      const store = useProjectStore.getState();
      if (phase === 'research') {
        store.setTriage(null as any);
        store.setSeparation(null as any);
        store.setResearchReport(null as any);
        store.setBoqExtraction(null as any);
        store.setPreScan(null);
      } else {
        store.setExtraction(null as any);
        store.setFloorPlanAnnotation(null as any);
      }
      useProjectStore.getState().setToast(`${label} results cleared — ready for re-run`, 'info');
    } catch (e) {
      useProjectStore.getState().setToast(`Re-run failed: ${e}`, 'error');
    }
    setRerunning(false);
  };

  return (
    <div className={`sidebar-skill-phase ${allDone ? 'phase-done' : ''}`}>
      <div className="sidebar-phase-label">
        {allDone && <span className="phase-check">&#10003;</span>}
        {label}
        <span className="phase-count">{done}/{skills.length}</span>
        {isOwner && !anyRunning && done > 0 && (
          <button
            className="btn-rerun-phase"
            onClick={handleRerun}
            disabled={rerunning}
            title={`Discard ${label.toLowerCase()} results and re-run`}
          >&#8635;</button>
        )}
      </div>
      <div className="sidebar-skills">
        {skills.map((name) => {
          const skill = statuses[name];
          const status = statusOf(name);
          const skillLabel = SKILL_LABELS[name] || name;
          return (
            <div key={name} className={`sidebar-skill-item status-${status}`} title={skill?.summary || ''}>
              <span className={`skill-dot ${status}`} />
              <span className="sidebar-skill-label">{skillLabel}</span>
              {status === 'running' && <RunningTimer startTime={skill?.startTime} />}
              {skill?.elapsed && status !== 'running' && (
                <span className="sidebar-skill-elapsed">{(skill.elapsed / 1000).toFixed(1)}s</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RunningTimer({ startTime }: { startTime?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!startTime) return;
    const iv = setInterval(() => {
      if (ref.current) {
        ref.current.textContent = `${((Date.now() - startTime) / 1000).toFixed(0)}s`;
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [startTime]);
  return <span ref={ref} className="sidebar-skill-elapsed">0s</span>;
}

function FileManifest() {
  const project = useProjectStore((s) => s.project);
  const files = project?.files;

  if (!files || files.length === 0) return null;

  return (
    <div className="sidebar-section">
      <div className="sidebar-section-label">Files</div>
      <div className="sidebar-files">
        {files.map((f) => (
          <div key={f.filename} className="sidebar-file-item">
            <span className="sidebar-file-name" title={f.filename}>
              {f.filename.length > 25 ? f.filename.slice(0, 22) + '...' : f.filename}
            </span>
            <span className="sidebar-file-pages">{f.page_count}p</span>
          </div>
        ))}
      </div>
    </div>
  );
}
